import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VideoCamera, Plus, XCircle, UserCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { fmtMeetingTime } from "../components/MyMeetingsCard";

function AttendanceEditor({ meeting, onClose, onSaved }) {
  const [checked, setChecked] = useState(() => new Set((meeting.attendees || []).filter((a) => a.attended).map((a) => a.uid)));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/attendance`, { attended_ids: [...checked] });
      toast.success(data.credited?.length
        ? `Attendance saved — training credit recorded for: ${data.credited.join(", ")}`
        : "Attendance saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid={`attendance-editor-${meeting.id}`}>
      <p className="text-xs font-semibold text-stone-700">Who attended?</p>
      {meeting.kind === "training" && (
        <p className="mt-0.5 text-[11px] text-[#8a5d14]">Everyone checked off receives a dated completion for “{meeting.topic_title}”.</p>
      )}
      <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
        {(meeting.attendees || []).map((a) => (
          <label key={a.uid} className="flex cursor-pointer items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm"
            data-testid={`att-${meeting.id}-${a.uid}`}>
            <input type="checkbox" checked={checked.has(a.uid)}
              onChange={(e) => {
                const next = new Set(checked);
                if (e.target.checked) next.add(a.uid); else next.delete(a.uid);
                setChecked(next);
              }} />
            <span className="truncate">{a.name}</span>
            {a.joined_at && <span className="ml-auto text-[10px] text-stone-400">joined</span>}
          </label>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={onClose} className="border-stone-300" data-testid={`att-cancel-${meeting.id}`}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`att-save-${meeting.id}`}>
          {busy ? "Saving…" : "Save attendance"}
        </Button>
      </div>
    </div>
  );
}

export default function MeetingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [topics, setTopics] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [attFor, setAttFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", kind: "staff", topic_id: "", start_at: "", duration_minutes: 60, location: "", notes: "", attendee_ids: [], is_orientation: false });

  const load = async () => {
    try {
      const { data } = await api.get("/meetings");
      setMeetings(data.meetings || []);
    } catch (e) { toast.error(formatApiError(e)); setMeetings([]); }
  };
  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/users").then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data.users || [];
        setStaff(list.filter((u) => u.role === "staff" && u.active !== false));
      }).catch(() => {});
      api.get("/training/topics").then((r) => setTopics(r.data.topics || r.data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const create = async () => {
    if (!form.title.trim() || !form.start_at) { toast.error("Title and date/time are required"); return; }
    if (form.kind === "training" && !form.topic_id) { toast.error("Pick the training topic this in-service covers"); return; }
    if (!form.attendee_ids.length) { toast.error("Pick at least one attendee"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/meetings", { ...form, duration_minutes: Number(form.duration_minutes) || 60 });
      toast.success(`Meeting scheduled — ${data.emailed} invite email(s) sent`);
      setShowForm(false);
      setForm({ title: "", kind: "staff", topic_id: "", start_at: "", duration_minutes: 60, location: "", notes: "", attendee_ids: [], is_orientation: false });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancel = async (m) => {
    if (!window.confirm(`Cancel "${m.title}"? Attendees will be emailed.`)) return;
    try {
      await api.post(`/meetings/${m.id}/cancel`);
      toast.success("Meeting canceled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const nowKey = new Date().toISOString().slice(0, 16);
  const todayKey = nowKey.slice(0, 10);
  const upcoming = (meetings || []).filter((m) => m.status !== "canceled" && (m.start_at || "") >= todayKey)
    .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
  const past = (meetings || []).filter((m) => !upcoming.includes(m))
    .sort((a, b) => (b.start_at || "").localeCompare(a.start_at || ""));

  const MeetingRow = ({ m }) => (
    <div className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`meeting-row-${m.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-stone-900">
            {m.title}
            {m.kind === "training" && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">IN-SERVICE · {m.topic_title}</span>}
            {m.status === "canceled" && <span className="ml-2 rounded-full bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]">CANCELED</span>}
            {m.status === "held" && <span className="ml-2 rounded-full bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]">HELD</span>}
          </p>
          <p className="mt-0.5 text-xs text-stone-500">
            {fmtMeetingTime(m.start_at)} · {m.duration_minutes} min · {(m.attendees || []).length} invited
            {m.location ? ` · ${m.location}` : " · video"}
          </p>
          {m.notes && (
            <details className="mt-1.5" data-testid={`meeting-agenda-${m.id}`}>
              <summary className="cursor-pointer text-xs font-semibold text-primary">Agenda</summary>
              <pre className="mt-1 whitespace-pre-wrap rounded-md bg-stone-50 p-3 font-sans text-xs leading-relaxed text-stone-600">{m.notes}</pre>
            </details>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {m.status !== "canceled" && (
            <Button size="sm" onClick={() => navigate(`/meetings/${m.id}`)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`meeting-join-${m.id}`}>
              <VideoCamera size={14} className="mr-1" /> Join
            </Button>
          )}
          {isAdmin && m.status === "scheduled" && (
            <>
              <Button size="sm" variant="outline" onClick={() => setAttFor(attFor === m.id ? null : m.id)} className="rounded-full border-stone-300" data-testid={`meeting-attendance-${m.id}`}>
                <UserCheck size={14} className="mr-1" /> Attendance
              </Button>
              <Button size="sm" variant="outline" onClick={() => cancel(m)} className="rounded-full border-stone-300 text-stone-400 hover:text-[#C0392B]" data-testid={`meeting-cancel-${m.id}`}>
                <XCircle size={14} />
              </Button>
            </>
          )}
          {isAdmin && m.status === "held" && (
            <Button size="sm" variant="outline" onClick={() => setAttFor(attFor === m.id ? null : m.id)} className="rounded-full border-stone-300" data-testid={`meeting-attendance-${m.id}`}>
              <UserCheck size={14} className="mr-1" /> Edit attendance
            </Button>
          )}
        </div>
      </div>
      {attFor === m.id && <AttendanceEditor meeting={m} onClose={() => setAttFor(null)} onSaved={() => { setAttFor(null); load(); }} />}
    </div>
  );

  return (
    <div className="max-w-3xl" data-testid="meetings-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Meetings</h1>
          <p className="mt-1 text-sm text-stone-500">
            Staff meetings and in-service trainings — video runs right here in the app, invites go out by email.
            In-service attendance counts toward training records.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="new-meeting-button">
            <Plus size={15} className="mr-1" /> Schedule a meeting
          </Button>
        )}
      </div>

      {isAdmin && showForm && (
        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="meeting-form">
          <p className="font-heading text-sm font-semibold text-stone-900">New meeting</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border-stone-300" data-testid="meeting-title-input" placeholder="e.g. September all-hands, or In-service: Infection Control" />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="flex gap-1.5" data-testid="meeting-kind-picker">
                {[["staff", "Staff meeting"], ["training", "In-service training"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium ${form.kind === k ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500"}`}
                    data-testid={`meeting-kind-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            {form.kind === "training" && (
              <div className="space-y-1.5">
                <Label>Training topic *</Label>
                <select value={form.topic_id} onChange={(e) => setForm({ ...form, topic_id: e.target.value })}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="meeting-topic-select">
                  <option value="">— pick topic —</option>
                  {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Date &amp; time *</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="border-stone-300" data-testid="meeting-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Duration (minutes)</Label>
              <Input type="number" min="15" max="480" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="border-stone-300" data-testid="meeting-duration-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Location (blank = video meeting in the app)</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border-stone-300" data-testid="meeting-location-input" placeholder="Office conference room, or leave blank for video" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes for the invite email</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-stone-300" data-testid="meeting-notes-input" />
            </div>
            <label className="flex items-center gap-2 rounded-md border border-stone-200 bg-stone-50 px-3 py-2.5 sm:col-span-2" data-testid="meeting-orientation-check">
              <input type="checkbox" checked={!!form.is_orientation} onChange={(e) => setForm({ ...form, is_orientation: e.target.checked })} data-testid="meeting-orientation-input" />
              <span className="text-sm text-stone-700"><strong>Orientation welcome meeting</strong> — attendees' "welcome meeting" orientation step checks off when you record their attendance</span>
            </label>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Attendees * ({form.attendee_ids.length} picked)</Label>
              <div className="grid max-h-48 gap-1 overflow-y-auto rounded-lg border border-stone-200 p-2 sm:grid-cols-2" data-testid="meeting-attendee-picker">
                {staff.map((s) => {
                  const uid = s.id || s._id;
                  const on = form.attendee_ids.includes(uid);
                  return (
                    <label key={uid} className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs ${on ? "bg-primary/10 font-medium text-primary" : "text-stone-600 hover:bg-stone-50"}`}
                      data-testid={`attendee-${uid}`}>
                      <input type="checkbox" checked={on}
                        onChange={(e) => setForm({ ...form, attendee_ids: e.target.checked ? [...form.attendee_ids, uid] : form.attendee_ids.filter((x) => x !== uid) })} />
                      {s.name || s.email}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-stone-300" data-testid="meeting-form-cancel">Cancel</Button>
            <Button onClick={create} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="meeting-form-submit">
              {busy ? "Scheduling…" : "Schedule & send invites"}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="meetings-upcoming-card">
        <p className="font-heading text-sm font-semibold text-stone-900">Upcoming</p>
        <div className="mt-3 space-y-2" data-testid="meetings-upcoming-list">
          {upcoming.length === 0 && <p className="text-xs text-stone-400" data-testid="meetings-upcoming-empty">Nothing scheduled.</p>}
          {upcoming.map((m) => <MeetingRow key={m.id} m={m} />)}
        </div>
      </div>

      {past.length > 0 && (
        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="meetings-past-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Past &amp; canceled</p>
          <div className="mt-3 space-y-2" data-testid="meetings-past-list">
            {past.slice(0, 20).map((m) => <MeetingRow key={m.id} m={m} />)}
          </div>
        </div>
      )}
    </div>
  );
}
